import {
  DEPARTMENTS_BY_CODE,
  NAME_FIRST_YEAR,
  type NameStats,
} from "@listou/shared/schemas/prenoms";

export const GIRLS = "#c4225a";
export const BOYS = "#1f6b52";
export const GRID = "#e6e4ee";

export const number = (value: number) => value.toLocaleString("fr-FR");
export const percent = (value: number, digits = 0) =>
  `${value.toLocaleString("fr-FR", { maximumFractionDigits: digits, minimumFractionDigits: digits })} %`;
export const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
export const ordinal = (rank: number) => (rank === 1 ? "1er" : `${rank}e`);
export const departmentName = (code: string) => DEPARTMENTS_BY_CODE.get(code)?.name ?? code;
export const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);

export type Gender = "girls" | "boys" | "mixed";
/** "Mixed" only when neither sex is a clear majority: Camille, Charlie, Sacha. */
export const genderOf = (data: Pick<NameStats, "girls" | "boys" | "total">): Gender => {
  const girls = data.girls / data.total;
  return girls >= 0.9 ? "girls" : girls <= 0.1 ? "boys" : "mixed";
};
export const genderWord = (gender: Gender) =>
  gender === "girls" ? "féminin" : gender === "boys" ? "masculin" : "mixte";

/** Year at which half of the births with this name were reached. */
export const medianYear = (years: number[]) => {
  const half = sum(years) / 2;
  let running = 0;
  for (const [index, count] of years.entries()) {
    running += count;
    if (running >= half) return NAME_FIRST_YEAR + index;
  }
  return NAME_FIRST_YEAR;
};

/** Consecutive years sharing the same leader, for the "reigns" ribbon. */
export type Reign = { slug: string; name: string; from: number; to: number };
export const reignsOf = (
  leaders: { year: number; leader: { slug: string; name: string } | null }[],
): Reign[] => {
  const reigns: Reign[] = [];
  for (const { year, leader } of leaders) {
    if (!leader) continue;
    const last = reigns.at(-1);
    if (last && last.slug === leader.slug && last.to === year - 1) last.to = year;
    else reigns.push({ slug: leader.slug, name: leader.name, from: year, to: year });
  }
  return reigns;
};

/** A stable color per name, in the order they first appear: identity never follows rank. */
/**
 * One color per name. Hues step by the golden angle, so any two names met one after the other
 * land far apart on the wheel however many there are; lightness stays fixed so white labels
 * remain readable on every one.
 */
export const nameColor = (index: number) =>
  `oklch(0.56 0.15 ${Math.round((index * 137.508 + 25) % 360)})`;
/** Fallback for a name with no assigned color. */
export const OTHER_COLOR = "#cfccd9";
